import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import {
  fetchShowcaseBets,
  fetchBetHouseDetail,
  selectShowcaseBets,
  selectBetHouseLoading,
  selectBetHouseError,
  selectBetHouseDetailById,
  selectBetHouseDetailLoadingId,
  selectBetHouseDetailError,
  selectFollowPending,
  toggleFollowBetHouse,
} from "../../redux/slices/betHouseSlice";
import { selectAppUser, selectAppAuthLoading } from "../redux/appAuthSlice";
import { isHouseOperator, toIdStr } from "../utils/houseAccess";
import { fetchOperatorHouses } from "../redux/operatorConsoleSlice";
import {
  getAboutHouse,
  getBoardSummary,
  getHeaderStats,
  getHousePulse,
  getOperators,
  getRecentStakes,
  getStakeRows,
  getTreasuryDetails,
  isFollowingHouse,
  isLowLiquidity,
  sortFixturesForSchedule,
} from "../utils/betDetailHelpers";
import DetailShell from "../components/betdetail/DetailShell";
import HouseAnalyticsDesk from "../components/betdetail/HouseAnalyticsDesk";
import {
  BoardSnapshotStrip,
  KickoffCountdown,
  LowLiquidityBanner,
} from "../components/betdetail/DetailSections";
import BetChatModal from "../components/bondhouse/BetChatModal";
import { PLACE_BET_PATH } from "../utils/bettingDeskHelpers";
import HouseTicketsRail from "../components/betdetail/HouseTicketsRail";
import "../betdetail.css";

const BetDetailFrame = ({ children }) => (
  <div className="bd-shell">
    <div className="bd-ambient" aria-hidden>
      <span className="bd-ambient-orb bd-ambient-orb-a" />
      <span className="bd-ambient-orb bd-ambient-orb-b" />
    </div>
    {children}
  </div>
);

const BetDetail = () => {
  const { houseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const houses = useSelector(selectShowcaseBets);
  const isLoading = useSelector(selectBetHouseLoading);
  const error = useSelector(selectBetHouseError);
  const detailHouse = useSelector(selectBetHouseDetailById(houseId));
  const detailLoadingId = useSelector(selectBetHouseDetailLoadingId);
  const detailError = useSelector(selectBetHouseDetailError(houseId));
  const followPending = useSelector(selectFollowPending);
  const user = useSelector(selectAppUser);
  const authLoading = useSelector(selectAppAuthLoading);
  const operatorHouses = useSelector((s) => s.operatorConsole.houses);
  const operatorHousesStatus = useSelector((s) => s.operatorConsole.housesStatus);
  const lastDetailFetchKeyRef = useRef(null);

  const houseIdStr = toIdStr(houseId);

  const house = useMemo(() => {
    if (!houseIdStr) return null;

    const fromShowcase = houses.find((h) => toIdStr(h?._id) === houseIdStr);
    if (fromShowcase) return fromShowcase;

    if (detailHouse && toIdStr(detailHouse._id) === houseIdStr) return detailHouse;

    const fromOperator = operatorHouses.find((h) => toIdStr(h?._id) === houseIdStr);
    if (fromOperator) return fromOperator;

    return null;
  }, [houses, houseIdStr, detailHouse, operatorHouses]);

  useEffect(() => {
    if (!houses.length) {
      dispatch(fetchShowcaseBets({ page: 1, limit: 30 }));
    }
  }, [houses.length, dispatch]);

  useEffect(() => {
    lastDetailFetchKeyRef.current = null;
  }, [houseIdStr]);

  useEffect(() => {
    if (!houseIdStr || authLoading) return;

    const inShowcase = houses.some((h) => toIdStr(h?._id) === houseIdStr);
    if (inShowcase) return;
    if (detailLoadingId === houseIdStr) return;

    const fetchKey = `${houseIdStr}:${user?._id || "anon"}`;
    if (lastDetailFetchKeyRef.current === fetchKey) return;
    lastDetailFetchKeyRef.current = fetchKey;

    dispatch(fetchBetHouseDetail({ betId: houseIdStr, userId: user?._id }));
  }, [
    houseIdStr,
    houses,
    detailLoadingId,
    user?._id,
    authLoading,
    dispatch,
  ]);

  useEffect(() => {
    if (!houseIdStr || !user?._id || authLoading) return;

    const inShowcase = houses.some((h) => toIdStr(h?._id) === houseIdStr);
    const inOperator = operatorHouses.some((h) => toIdStr(h?._id) === houseIdStr);
    if (inShowcase || inOperator || operatorHousesStatus === "loading") return;

    dispatch(fetchOperatorHouses(user._id));
  }, [
    houseIdStr,
    user?._id,
    authLoading,
    houses,
    operatorHouses,
    operatorHousesStatus,
    dispatch,
  ]);

  const fixtures = useMemo(
    () => sortFixturesForSchedule((house?.fixtures || []).filter((f) => f && f._id)),
    [house]
  );

  const headerStats = useMemo(() => getHeaderStats(house), [house]);
  const pulse = useMemo(() => getHousePulse(house), [house]);
  const treasury = useMemo(() => getTreasuryDetails(house), [house]);
  const about = useMemo(() => getAboutHouse(house), [house]);
  const stakeRows = useMemo(() => getStakeRows(house, fixtures.length), [house, fixtures.length]);
  const showLowLiquidity = useMemo(() => isLowLiquidity(house), [house]);
  const board = useMemo(() => getBoardSummary(fixtures), [fixtures]);
  const operators = useMemo(() => getOperators(house), [house]);
  const recentStakes = useMemo(() => getRecentStakes(house, 12), [house]);

  const canManage = useMemo(
    () => isHouseOperator(house, user?._id),
    [house, user?._id]
  );

  const isFollowing = useMemo(
    () => isFollowingHouse(house, user?._id),
    [house, user?._id]
  );

  const hasFixtures = fixtures.length > 0;

  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("action") === "chat") setChatOpen(true);
  }, [searchParams]);

  useEffect(() => {
    if (
      searchParams.get("action") === "place" &&
      house?._id &&
      !canManage &&
      hasFixtures
    ) {
      navigate(PLACE_BET_PATH(house._id), { replace: true });
    }
  }, [searchParams, house?._id, canManage, hasFixtures, navigate]);

  const handleBack = useCallback(() => navigate("/app/bethouse"), [navigate]);

  const handlePrimary = useCallback(() => {
    if (!house) return;
    if (canManage) {
      navigate(`/app/bethouse/${house._id}/manage`);
      return;
    }
    if (!hasFixtures) return;
    navigate(PLACE_BET_PATH(house._id));
  }, [house, canManage, hasFixtures, navigate]);

  const handleFollow = useCallback(() => {
    if (!house?._id) return;
    if (!user?._id) {
      navigate(`/app/login`, { state: { from: `/app/bethouse/${house._id}` } });
      return;
    }
    dispatch(toggleFollowBetHouse({ userId: user._id, betId: house._id }));
  }, [house, user?._id, dispatch, navigate]);

  const showLoading =
    authLoading ||
    (!house && (isLoading || detailLoadingId === houseIdStr));
  const showMissing = !house && !showLoading;

  const missingMessage = useMemo(() => {
    if (detailError?.status === 403) {
      return "You don't have permission to view this house. Sign in or follow the house if it is followers-only.";
    }
    if (detailError?.status === 404) {
      return "This house doesn't exist or may have been removed.";
    }
    if (error || detailError?.message) {
      return "We couldn't load this house right now.";
    }
    return "This house may be unpublished or doesn't exist.";
  }, [detailError, error]);

  if (showLoading) {
    return (
      <BetDetailFrame>
        <div className="bd-scroll bd-scroll-center">
          <div className="bd-loading">
            <Loader2 size={18} className="animate-spin" />
            <span>Loading house</span>
          </div>
        </div>
      </BetDetailFrame>
    );
  }

  if (showMissing) {
    return (
      <BetDetailFrame>
        <DetailShell
          houseName="Unavailable"
          canManage={false}
          onChat={() => {}}
          onPrimary={handleBack}
        />
        <div className="bd-scroll">
          <div className="bd-page-inner">
            <div className="bd-empty">
              <p className="bd-empty-title">House not available</p>
              <p className="bd-empty-sub">{missingMessage}</p>
            </div>
          </div>
        </div>
      </BetDetailFrame>
    );
  }

  return (
    <BetDetailFrame>
      <div className="bd-desk-top">
        <DetailShell
          house={house}
          houseName={house.name}
          canManage={canManage}
          fixtureCount={fixtures.length}
          onChat={() => setChatOpen(true)}
          onPrimary={handlePrimary}
          stats={headerStats}
          isFollowing={isFollowing}
          onFollowClick={handleFollow}
          followDisabled={followPending}
        />
      </div>

      <div className="bd-scroll">
        <KickoffCountdown fixtures={fixtures} />
        <BoardSnapshotStrip board={board} viewsCount={pulse.viewsCount} />

        <div className="bd-page-inner">
          {showLowLiquidity ? <LowLiquidityBanner /> : null}

          <div className="bd-detail-layout">
            <div className="bd-tickets-rail">
              <HouseTicketsRail
                houseId={house._id}
                canManage={canManage}
                seedCount={pulse.totalTickets}
              />
            </div>

            <div className="bd-detail-body">
              <HouseAnalyticsDesk
                house={house}
                fixtures={fixtures}
                pulse={pulse}
                treasury={treasury}
                recentStakes={recentStakes}
                about={about}
                operators={operators}
                stakeRows={stakeRows}
              />
            </div>
          </div>
        </div>
      </div>

      <BetChatModal open={chatOpen} onClose={() => setChatOpen(false)} house={house} />
    </BetDetailFrame>
  );
};

export default BetDetail;
